import {Listbox, ListboxButton, ListboxOption, ListboxOptions} from '@headlessui/react';
import {type ComponentProps, type ReactNode, useId} from 'react';
import {PepIcon} from './PepIcon';
import classNames from 'classnames';
import {type ColorName} from '#/cms/schemas/theme-colors.schema';
import {BodyText} from './Text';

export type Option = {
  id: string;
  title: string;
};

type Props<T extends Option = Option> = Omit<ComponentProps<typeof Listbox>, 'onChange' | 'value' | 'className'> & {
  title?: ReactNode;
  value: T | undefined | null;
  options: T[];
  border?: ColorName;
  optionsStyle?: string;
  className?: string;
  onChange?: (value: T) => void;
};

const Select = <T extends Option>({
  title,
  options,
  value,
  border = 'midground3',
  optionsStyle,
  className,
  onChange,
  ...props
}: Props<T>) => {
  const borderColorStyle = `border-${border}`;
  const selectId = useId();
  return (
    <Listbox value={value ?? undefined} onChange={onChange as (value: unknown) => void} {...props}>
      <div className={classNames('relative', className)}>
        <ListboxButton
          className={classNames(
            'flex w-full items-center justify-between rounded-full border px-4 py-2 text-left',
            borderColorStyle
          )}
        >
          <BodyText>{value?.title ?? title ?? ''}</BodyText>
          <PepIcon iconName="ChevronDown" strokeColor="midground4" />
        </ListboxButton>

        <ListboxOptions
          className={classNames(
            'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-2xl border bg-background pl-0',
            borderColorStyle,
            optionsStyle
          )}
        >
          {options.map((option, idx) => (
            <ListboxOption
              key={`${selectId}-option-${idx}`}
              style={{}}
              className={classNames(
                'cursor-pointer list-none py-1 pl-4 data-[focus]:bg-midground2 data-[selected]:bg-primary hover:bg-midground2'
              )}
              value={option}
            >
              <BodyText className={value?.id === option.id ? 'text-light' : 'text-foreground'}>{option.title}</BodyText>
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
};

export default Select;
